(function () {
  'use strict';

  // ─── CONFIG ──────────────────────────────────────────────────────
  const IMS = {
    version: '2.1.0',
    appName: 'StockMaestro',
    developer: 'ABSA Digital',
    delayedDays: 5,
    claimAlertDays: 14,
    claimEscalateDays: 30,
  };

  // ─── SUPABASE CLIENT ─────────────────────────────────────────────
  let _sb = null;

  function sb() {
    if (_sb) return _sb;
    const url = window.IMS_SUPABASE_URL;
    const key = window.IMS_SUPABASE_ANON_KEY;
    if (!url || !key) {
      console.error('[StockMaestro] Supabase credentials missing');
      return null;
    }
    try {
      _sb = window.supabase.createClient(url, key, {
        auth: { persistSession: true, autoRefreshToken: true }
      });
    } catch (e) {
      console.error('[StockMaestro] Failed to create Supabase client:', e);
      return null;
    }
    return _sb;
  }

  // ─── AUTH ─────────────────────────────────────────────────────────
  const AUTH = {
    user: null,
    company: null,
    session: null,

    async signIn(email, password) {
      const client = sb();
      if (!client) return { error: 'Connection error — check Supabase credentials' };
      const { data, error } = await client.auth.signInWithPassword({ email, password });
      if (error) return { error: error.message };
      this.session = data.session;
      const ok = await this.loadProfile(data.user.id);
      if (!ok) return { error: 'Account not found. Please contact your administrator.' };
      return { success: true };
    },

    async signOut() {
      const client = sb();
      if (client) await client.auth.signOut();
      this.user = null;
      this.company = null;
      this.session = null;
      UI.showLogin();
    },

    async loadProfile(authId) {
      const client = sb();
      if (!client) return false;
      let { data, error } = await client
        .from('users')
        .select('*, companies(*)')
        .eq('auth_id', authId)
        .eq('is_active', true)
        .maybeSingle();
      if (error) {
        console.error('[StockMaestro] Profile load error:', error.message);
        return false;
      }
      if (!data) {
        // The previous client-side email-fallback lookup here always
        // failed silently under RLS: users_select only allows
        // (auth_id = auth.uid()) OR (company_id = get_user_company_id()),
        // and get_user_company_id() itself resolves via a users lookup
        // keyed on auth_id — so for a brand-new unlinked row (auth_id IS
        // NULL), BOTH halves of that OR return null/false from the
        // client's own session. No client-side query could ever see this
        // row, regardless of how it was written. link_my_account() is a
        // SECURITY DEFINER function that performs this one linking step
        // server-side, safely bypassing that chicken-and-egg RLS gap for
        // exactly this narrow bootstrap purpose only.
        const { data: linkedUserId, error: rpcErr } = await client.rpc('link_my_account');
        if (!rpcErr && linkedUserId) {
          const { data: relinked } = await client
            .from('users')
            .select('*, companies(*)')
            .eq('auth_id', authId)
            .eq('is_active', true)
            .maybeSingle();
          if (relinked) data = relinked;
        }
      }
      if (!data) {
        console.warn('[StockMaestro] No active user found for auth_id:', authId);
        return false;
      }
      this.user = data;
      this.company = data.companies;
      client.from('users').update({ last_login: new Date().toISOString() }).eq('id', data.id).then(({error})=>{ if(error) console.warn('[StockMaestro] last_login update failed:',error.message); });
      return true;
    },

    async checkSession() {
      const client = sb();
      if (!client) return false;
      const { data: { session }, error } = await client.auth.getSession();
      if (error || !session) return false;
      this.session = session;
      return await this.loadProfile(session.user.id);
    },

    hasRole(...roles) {
      return roles.includes(this.user?.role);
    },

    isAdmin() {
      return ['super_admin', 'admin'].includes(this.user?.role);
    },

    isSuperAdmin() {
      return this.user?.role === 'super_admin';
    },

    canSeeCost() {
      return ['super_admin', 'admin', 'imports_manager'].includes(this.user?.role);
    },

    canAccess(module) {
      const role = this.user?.role;
      if (!role) return false;
      if (['super_admin', 'admin'].includes(role)) return true;
      if (role === 'custom') {
        // Per-user granular access — any module can be individually
        // granted here, including admin-area modules (Company/Backup/
        // Users), per explicit design decision. Destructive/admin-only
        // actions elsewhere in the app (gated by isAdmin(), not this
        // function) remain blocked for this role regardless of what's
        // checked here, since isAdmin() only ever matches
        // 'super_admin'/'admin' — unaffected by this change.
        return (this.user?.custom_permissions || []).includes(module);
      }
      const map = {
        imports_manager:   ['imports'],
        warehouse_manager: ['inventory', 'warehouse', 'logistics', 'purchases'],
        warehouse_staff:   ['inventory', 'warehouse'],
        order_management:  ['orders', 'customers', 'logistics'],
        logistics_staff:   ['logistics', 'warehouse'],
        accounts_staff:    ['accounts', 'reports'],
        viewer:            ['dashboard', 'reports'],
      };
      return (map[role] || []).includes(module);
    }
  };

  // ─── LICENCE CHECK ────────────────────────────────────────────────
  // Called after profile loaded. Returns: 'active' | 'grace' | 'suspended' | 'expired'
  // Shows lockout screen for suspended/expired, warning banner for grace period.
  async function checkLicence() {
    const client = sb();
    if (!client || !AUTH.company?.id) return 'active'; // fail open if no connection

    const { data: lic, error } = await client
      .from('licences')
      .select('*')
      .eq('company_id', AUTH.company.id)
      .maybeSingle();

    // No licence record = allow during development, warn in console
    if (error || !lic) {
      console.warn('[StockMaestro] No licence found for company — allowing access');
      return 'active';
    }

    const today = new Date();
    const expiresAt = new Date(lic.expires_at);
    const graceDays = lic.grace_days || 7;
    const graceEnd = new Date(expiresAt);
    graceEnd.setDate(graceEnd.getDate() + graceDays);

    // Suspended — hard lock regardless of dates
    if (lic.status === 'suspended') {
      showLockoutScreen('suspended', lic);
      return 'suspended';
    }

    // Explicitly expired in DB
    if (lic.status === 'expired') {
      showLockoutScreen('expired', lic);
      return 'expired';
    }

    // Past grace period — hard lock
    if (today > graceEnd) {
      showLockoutScreen('expired', lic);
      return 'expired';
    }

    // Within grace period — allow but show warning banner
    if (today > expiresAt) {
      const daysLeft = Math.ceil((graceEnd - today) / 86400000);
      showGraceBanner(daysLeft, lic);
      return 'grace';
    }

    // Check expiry warning (14 days before)
    const warningDate = new Date(expiresAt);
    warningDate.setDate(warningDate.getDate() - 14);
    if (today >= warningDate) {
      const daysLeft = Math.ceil((expiresAt - today) / 86400000);
      showExpiryWarning(daysLeft);
    }

    return 'active';
  }

  function showLockoutScreen(reason, lic) {
    // Hide app and login, show lockout
    document.getElementById('ims-login-screen').style.display = 'none';
    document.getElementById('ims-app').style.display = 'none';

    let existing = document.getElementById('ims-lockout-screen');
    if (!existing) {
      existing = document.createElement('div');
      existing.id = 'ims-lockout-screen';
      document.body.appendChild(existing);
    }

    const isSuspended = reason === 'suspended';
    const title = isSuspended ? 'Account Suspended' : 'Subscription Expired';
    const icon  = isSuspended ? 'ti-lock' : 'ti-clock-off';
    const msg   = isSuspended
      ? (lic.suspension_reason || 'Your StockMaestro account has been suspended.')
      : 'Your StockMaestro subscription has expired. Please renew to continue.';

    existing.innerHTML = `
      <style>
        #ims-lockout-screen{
          position:fixed;inset:0;background:#1B3A6B;
          display:flex;align-items:center;justify-content:center;
          z-index:999999;font-family:'Inter',system-ui,sans-serif;padding:20px
        }
        .ims-lock-card{
          background:#fff;border-radius:16px;padding:48px 40px;
          max-width:460px;width:100%;text-align:center;
          box-shadow:0 20px 60px rgba(0,0,0,.4)
        }
        .ims-lock-icon{font-size:56px;color:#DC2626;margin-bottom:16px}
        .ims-lock-title{font-size:22px;font-weight:700;color:#111;margin-bottom:8px}
        .ims-lock-msg{font-size:14px;color:#6B7280;line-height:1.6;margin-bottom:24px}
        .ims-lock-contact{
          background:#FEF2F2;border:1px solid #FECACA;
          border-radius:8px;padding:14px;font-size:13px;color:#DC2626;margin-bottom:20px
        }
        .ims-lock-brand{font-size:11px;color:#9CA3AF;margin-top:20px}
        .ims-lock-brand strong{color:#1B3A6B}
      </style>
      <div class="ims-lock-card">
        <div class="ims-lock-icon"><i class="ti ${icon}"></i></div>
        <div class="ims-lock-title">${title}</div>
        <div class="ims-lock-msg">${msg}</div>
        <div class="ims-lock-contact">
          <strong>To restore access, contact:</strong><br>
          ABSA Digital &nbsp;|&nbsp; hafimohsin.me@gmail.com<br>
          +44 774 290 712
        </div>
        <div class="ims-lock-brand">
          <strong>StockMaestro</strong> &copy; 2026 ABSA Digital
        </div>
      </div>`;
  }

  function showGraceBanner(daysLeft, lic) {
    // Show app normally but inject a prominent warning banner
    const banner = document.createElement('div');
    banner.id = 'ims-grace-banner';
    banner.style.cssText = [
      'position:fixed','top:0','left:0','right:0','z-index:999999',
      'background:#DC2626','color:#fff','font-size:13px','font-weight:500',
      'padding:10px 20px','display:flex','align-items:center','gap:10px',
      'justify-content:center','font-family:Inter,system-ui,sans-serif'
    ].join(';');
    banner.innerHTML = `
      <i class="ti ti-alert-triangle" style="font-size:16px"></i>
      <span>Your StockMaestro subscription expired. You have <strong>${daysLeft} day${daysLeft!==1?'s':''}</strong> of grace access remaining. Contact ABSA Digital to renew.</span>
      <a href="mailto:hafimohsin.me@gmail.com" style="color:#fff;font-weight:700;margin-left:8px;text-decoration:underline">Renew now</a>
      <button onclick="this.parentElement.remove()" style="margin-left:auto;background:none;border:none;color:#fff;font-size:18px;cursor:pointer;line-height:1">&times;</button>`;
    document.body.prepend(banner);

    // Push app down so banner doesn't overlap
    const app = document.getElementById('ims-app');
    if (app) app.style.paddingTop = '48px';
    const shopifyBar = document.querySelector('.shopify-section-header-sticky, #shopify-section-header, .site-header, .header-section');
    if(shopifyBar) banner.style.top = shopifyBar.offsetHeight + 'px';
  }

  function showExpiryWarning(daysLeft) {
    // Subtle warning in the app — dismissible
    setTimeout(() => {
      UI.toast(
        `Your StockMaestro licence expires in ${daysLeft} day${daysLeft!==1?'s':''}. Contact ABSA Digital to renew.`,
        'warning',
        8000
      );
    }, 2000);
  }

  // ─── DATABASE ─────────────────────────────────────────────────────
  const DB = {
    async select(table, opts = {}) {
      const client = sb();
      if (!client) return { data: null, error: 'No connection' };
      let q = client.from(table).select(opts.select || '*');
      if (!opts.noCompanyFilter && AUTH.company?.id) {
        q = q.eq('company_id', AUTH.company.id);
      }
      if (opts.eq) {
        Object.entries(opts.eq).forEach(([k, v]) => { q = q.eq(k, v); });
      }
      if (opts.neq) {
        Object.entries(opts.neq).forEach(([k, v]) => { q = q.neq(k, v); });
      }
      if (opts.in) {
        Object.entries(opts.in).forEach(([k, v]) => { q = q.in(k, v); });
      }
      if (opts.ilike) {
        Object.entries(opts.ilike).forEach(([k, v]) => { q = q.ilike(k, `%${v}%`); });
      }
      if (opts.gte) {
        Object.entries(opts.gte).forEach(([k, v]) => { q = q.gte(k, v); });
      }
      if (opts.lte) {
        Object.entries(opts.lte).forEach(([k, v]) => { q = q.lte(k, v); });
      }
      if (opts.is) {
        Object.entries(opts.is).forEach(([k, v]) => { q = q.is(k, v); });
      }
      if (opts.order) {
        const orders = Array.isArray(opts.order) ? opts.order : [opts.order];
        orders.forEach(o => { q = q.order(o.col, { ascending: o.asc !== false }); });
      }
      if (opts.limit) q = q.limit(opts.limit);
      if (opts.single) q = q.single();
      if (opts.maybeSingle) q = q.maybeSingle();
      return q;
    },

    async insert(table, data) {
      const client = sb();
      if (!client) return { data: null, error: 'No connection' };
      const payload = AUTH.company?.id ? { ...data, company_id: AUTH.company.id } : data;
      return client.from(table).insert(payload).select().single();
    },

    async insertMany(table, rows) {
      const client = sb();
      if (!client) return { data: null, error: 'No connection' };
      const payload = AUTH.company?.id
        ? rows.map(r => ({ ...r, company_id: AUTH.company.id }))
        : rows;
      return client.from(table).insert(payload).select();
    },

    async update(table, id, data) {
      const client = sb();
      if (!client) return { data: null, error: 'No connection' };
      return client.from(table).update(data).eq('id', id).select().single();
    },

    async updateWhere(table, filters, data) {
      const client = sb();
      if (!client) return { data: null, error: 'No connection' };
      let q = client.from(table).update(data);
      Object.entries(filters).forEach(([k, v]) => { q = q.eq(k, v); });
      return q.select();
    },

    async remove(table, id) {
      const client = sb();
      if (!client) return { error: 'No connection' };
      return client.from(table).delete().eq('id', id);
    },

    async upsert(table, data, onConflict) {
      const client = sb();
      if (!client) return { data: null, error: 'No connection' };
      return client.from(table).upsert(data, { onConflict }).select();
    },

    async getProductPrice(productId, priceType, platformId = null) {
      const client = sb();
      if (!client) return null;
      let q = client.from('product_prices')
        .select('price')
        .eq('product_id', productId)
        .eq('price_type', priceType);
      if (platformId) {
        q = q.eq('platform_id', platformId);
      } else {
        q = q.is('platform_id', null);
      }
      const { data } = await q.maybeSingle();
      return data?.price ?? null;
    },

    async getProductPrices(productId) {
      const client = sb();
      if (!client) return [];
      const { data } = await client
        .from('product_prices')
        .select('*, platforms(name)')
        .eq('product_id', productId);
      return data || [];
    },

    async getAvailableStock(productId, warehouseId = null) {
      let q = { select: 'quantity,reserved_qty', eq: { product_id: productId }, noCompanyFilter: true };
      if (warehouseId) q.eq.warehouse_id = warehouseId;
      const { data } = await this.select('stock_levels', q);
      if (!data) return 0;
      const levels = Array.isArray(data) ? data : [data];
      return levels.reduce((sum, l) => sum + ((l.quantity || 0) - (l.reserved_qty || 0)), 0);
    },

    async getPlatformTerms(platformId) {
      const client = sb();
      if (!client) return null;
      const { data } = await client
        .from('platform_terms')
        .select('*')
        .eq('platform_id', platformId)
        .maybeSingle();
      return data;
    }
  };

  // ─── UI CONTROLLER ────────────────────────────────────────────────
  const UI = {
    activePanel: 'dashboard',

    init() {
      this.bindNav();
      this.bindMobileToggle();
      this.bindModal();
      this.bindThemeToggle();
      window.addEventListener('hashchange', () => {
        const hash = location.hash.replace('#', '');
        if (hash) this.showPanel(hash);
      });
    },

    showLogin() {
      document.getElementById('ims-login-screen').style.display = 'flex';
      document.getElementById('ims-setpw-screen').style.display = 'none';
      document.getElementById('ims-app').style.display = 'none';
    },

    showSetPassword() {
      document.getElementById('ims-login-screen').style.display = 'none';
      document.getElementById('ims-app').style.display = 'none';
      document.getElementById('ims-setpw-screen').style.display = 'flex';
    },

    showApp() {
      document.getElementById('ims-login-screen').style.display = 'none';
      document.getElementById('ims-setpw-screen').style.display = 'none';
      document.getElementById('ims-app').style.display = 'flex';
      this.applyTheme(AUTH.user?.theme_pref || 'light');
      this.updateUserDisplay();
      this.filterNavByRole();
    },

    showPanel(panelId) {
      document.querySelectorAll('.ims__panel').forEach(p => p.classList.remove('ims__panel--active'));
      const target = document.getElementById('ims-panel-' + panelId);
      if (!target) return;
      target.classList.add('ims__panel--active');
      this.activePanel = panelId;
      location.hash = panelId;
      this.updateNavActive(panelId);
      this.closeSidebar();
      const titles = {
        dashboard: 'Dashboard', orders: 'Order management',
        inventory: 'Inventory & stock', imports: 'Imports',
        customers: 'Customer management', purchases: 'Purchase orders',
        warehouse: 'Warehouse', logistics: 'Logistics',
        accounts: 'Accounts', reports: 'Reports',
        company: 'Company setup', users: 'Users',
        backup: 'Backup & restore', platforms: 'Platforms'
      };
      const titleEl = document.getElementById('ims-page-title');
      if (titleEl) titleEl.textContent = titles[panelId] || panelId;
      window.dispatchEvent(new CustomEvent('ims:panel', { detail: { panel: panelId } }));
    },

    updateNavActive(panelId) {
      document.querySelectorAll('.ims__nav-item').forEach(item => {
        item.classList.toggle('ims__nav-item--active', item.dataset.panel === panelId);
      });
    },

    bindNav() {
      document.querySelectorAll('.ims__nav-item[data-panel]').forEach(item => {
        item.addEventListener('click', () => this.showPanel(item.dataset.panel));
        item.addEventListener('keydown', e => { if (e.key === 'Enter' || e.key === ' ') this.showPanel(item.dataset.panel); });
      });
    },

    bindMobileToggle() {
      const btn = document.getElementById('ims-hamburger');
      const overlay = document.getElementById('ims-overlay');
      if (btn) btn.addEventListener('click', () => this.toggleSidebar());
      if (overlay) overlay.addEventListener('click', () => this.closeSidebar());
    },

    toggleSidebar() {
      document.getElementById('ims-sidebar')?.classList.toggle('ims__sidebar--open');
      document.getElementById('ims-overlay')?.classList.toggle('ims__overlay--show');
    },

    closeSidebar() {
      document.getElementById('ims-sidebar')?.classList.remove('ims__sidebar--open');
      document.getElementById('ims-overlay')?.classList.remove('ims__overlay--show');
    },

    bindModal() {
      document.getElementById('ims-modal-close')?.addEventListener('click', () => this.closeModal());
      document.getElementById('ims-modal-overlay')?.addEventListener('click', e => {
        if (e.target.id === 'ims-modal-overlay') this.closeModal();
      });
      document.addEventListener('keydown', e => {
        if (e.key === 'Escape') this.closeModal();
      });
    },

    openModal(title, bodyHtml, footerHtml = '', size = '') {
      const el = document.getElementById('ims-modal');
      const overlay = document.getElementById('ims-modal-overlay');
      if (!el || !overlay) return;
      document.getElementById('ims-modal-title').textContent = title;
      document.getElementById('ims-modal-body').innerHTML = bodyHtml;
      document.getElementById('ims-modal-footer').innerHTML = footerHtml;
      el.className = 'ims__modal' + (size ? ' ims__modal--' + size : '');
      overlay.classList.add('ims__modal-overlay--open');
      setTimeout(() => el.querySelector('input,select,textarea')?.focus(), 100);
    },

    closeModal() {
      document.getElementById('ims-modal-overlay')?.classList.remove('ims__modal-overlay--open');
    },

    confirm(message, onConfirm, confirmLabel = 'Confirm', danger = false) {
      this.openModal('Confirm', `<p style="font-size:14px;color:var(--ims-text-secondary)">${message}</p>`,
        `<button class="ims__btn ims__btn--ghost" onclick="window.SM.UI.closeModal()">Cancel</button>
         <button class="ims__btn ims__btn--${danger ? 'danger' : 'primary'}" id="ims-confirm-btn">${confirmLabel}</button>`,
        'sm'
      );
      document.getElementById('ims-confirm-btn')?.addEventListener('click', () => {
        this.closeModal();
        onConfirm();
      });
    },

    toast(message, type = 'info', duration = 3500) {
      const container = document.getElementById('ims-toasts');
      if (!container) return;
      const el = document.createElement('div');
      el.className = `ims__toast ims__toast--${type}`;
      const icons = { success: 'check-circle', error: 'alert-circle', warning: 'alert-triangle', info: 'info-circle' };
      el.innerHTML = `<i class="ti ti-${icons[type] || 'info-circle'}" aria-hidden="true"></i><span>${UTIL.sanitize(message)}</span>
        <button class="ims__toast-close" onclick="this.parentElement.remove()" aria-label="Close">&times;</button>`;
      container.appendChild(el);
      requestAnimationFrame(() => el.classList.add('ims__toast--show'));
      setTimeout(() => { el.classList.remove('ims__toast--show'); setTimeout(() => el.remove(), 300); }, duration);
    },

    loading(containerId, msg = 'Loading...') {
      const el = document.getElementById(containerId);
      if (el) el.innerHTML = `<div class="ims__loading"><i class="ti ti-loader-2 ims__spin" aria-hidden="true"></i><span>${UTIL.sanitize(msg)}</span></div>`;
    },

    empty(containerId, msg = 'No records found', icon = 'inbox') {
      const el = document.getElementById(containerId);
      if (el) el.innerHTML = `<div class="ims__empty"><i class="ti ti-${icon}" aria-hidden="true"></i><p>${UTIL.sanitize(msg)}</p></div>`;
    },

    updateUserDisplay() {
      const u = AUTH.user;
      const c = AUTH.company;
      if (!u) return;
      const set = (id, val) => { const el = document.getElementById(id); if (el) el.textContent = val; };
      set('ims-user-name', u.name || u.email);
      set('ims-user-role', UTIL.formatRole(u.role));
      set('ims-user-avatar', UTIL.initials(u.name || u.email));
      set('ims-company-name', c?.name || IMS.appName);
      set('ims-topbar-date', new Intl.DateTimeFormat('en-GB', {
        weekday: 'short', day: '2-digit', month: 'short', year: 'numeric'
      }).format(new Date()));
    },

    filterNavByRole() {
      document.querySelectorAll('.ims__nav-item[data-module]').forEach(item => {
        const mod = item.dataset.module;
        item.style.display = AUTH.canAccess(mod) ? '' : 'none';
      });
    },

    updateBadge(id, count) {
      const el = document.getElementById(id);
      if (!el) return;
      if (count > 0) { el.textContent = count > 99 ? '99+' : count; el.style.display = 'flex'; }
      else { el.style.display = 'none'; }
    },

    applyTheme(theme) {
      document.getElementById('ims-app')?.setAttribute('data-theme', theme);
      const btn = document.getElementById('ims-theme-toggle');
      if (btn) {
        btn.querySelector('i').className = theme === 'dark' ? 'ti ti-sun' : 'ti ti-moon';
        btn.setAttribute('aria-label', theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode');
      }
    },

    bindThemeToggle() {
      document.getElementById('ims-theme-toggle')?.addEventListener('click', async () => {
        const current = document.getElementById('ims-app')?.getAttribute('data-theme') || 'light';
        const next = current === 'light' ? 'dark' : 'light';
        this.applyTheme(next);
        if (AUTH.user?.id) {
          await DB.update('users', AUTH.user.id, { theme_pref: next });
          AUTH.user.theme_pref = next;
        }
      });
    },

    async loadNotifications() {
      const { data: notes } = await DB.select('notifications', {
        eq: { is_read: false },
        order: { col: 'created_at', asc: false },
        limit: 20
      });
      const count = notes?.length || 0;
      this.updateBadge('ims-notif-badge', count);
      const dot = document.getElementById('ims-notif-dot');
      if (dot) dot.style.display = count > 0 ? 'block' : 'none';
      return notes || [];
    },

    autocomplete(inputEl, fetchFn, onSelect) {
      if (!inputEl) return;
      const parent = inputEl.parentElement;
      let dropdown = parent.querySelector('.ims__ac-dropdown');
      if (!dropdown) {
        dropdown = document.createElement('div');
        dropdown.className = 'ims__ac-dropdown';
        parent.style.position = 'relative';
        parent.appendChild(dropdown);
      }
      const search = UTIL.debounce(async (val) => {
        if (!val || val.length < 2) { dropdown.style.display = 'none'; return; }
        const results = await fetchFn(val);
        if (!results?.length) { dropdown.style.display = 'none'; return; }
        dropdown.innerHTML = results.map((r, i) =>
          `<div class="ims__ac-item" data-i="${i}">${UTIL.sanitize(r.label)}</div>`
        ).join('');
        dropdown.style.display = 'block';
        dropdown.querySelectorAll('.ims__ac-item').forEach((item, i) => {
          item.addEventListener('mousedown', e => {
            e.preventDefault();
            inputEl.value = results[i].label;
            dropdown.style.display = 'none';
            onSelect(results[i]);
          });
        });
      }, 280);
      inputEl.addEventListener('input', e => search(e.target.value));
      inputEl.addEventListener('blur', () => setTimeout(() => { dropdown.style.display = 'none'; }, 200));
      document.addEventListener('click', e => { if (!parent.contains(e.target)) dropdown.style.display = 'none'; });
    }
  };

  // ─── UTILITIES ────────────────────────────────────────────────────
  const UTIL = {
    formatCurrency(amount, currency = 'GBP') {
      return new Intl.NumberFormat('en-GB', {
        style: 'currency', currency, minimumFractionDigits: 2
      }).format(Number(amount) || 0);
    },

    formatDate(dateStr) {
      if (!dateStr) return '—';
      try { return new Intl.DateTimeFormat('en-GB').format(new Date(dateStr)); }
      catch (e) { return '—'; }
    },

    formatDateTime(dateStr) {
      if (!dateStr) return '—';
      try {
        return new Intl.DateTimeFormat('en-GB', {
          day: '2-digit', month: '2-digit', year: 'numeric',
          hour: '2-digit', minute: '2-digit'
        }).format(new Date(dateStr));
      } catch (e) { return '—'; }
    },

    formatRole(role) {
      const map = {
        super_admin: 'Super Admin', admin: 'Admin',
        imports_manager: 'Imports Manager', warehouse_manager: 'Warehouse Manager',
        warehouse_staff: 'Warehouse Staff', order_management: 'Order Management',
        logistics_staff: 'Logistics Staff', accounts_staff: 'Accounts Staff', viewer: 'Viewer',
        custom: 'Custom'
      };
      return map[role] || role || '—';
    },

    initials(name) {
      if (!name) return '??';
      return name.trim().split(/\s+/).map(w => w[0]).join('').toUpperCase().slice(0, 2);
    },

    generateRef(prefix, lastRef) {
      const num = lastRef ? (parseInt(lastRef.split('-').pop()) || 0) + 1 : 1;
      return `${prefix}-${String(num).padStart(5, '0')}`;
    },

    daysSince(dateStr) {
      if (!dateStr) return 0;
      return Math.floor((Date.now() - new Date(dateStr).getTime()) / 86400000);
    },

    isDelayed(order) {
      return order.status === 'dispatched' && this.daysSince(order.dispatched_at) >= IMS.delayedDays;
    },

    sanitize(str) {
      if (str === null || str === undefined) return '';
      const el = document.createElement('div');
      el.textContent = String(str);
      return el.innerHTML;
    },

    debounce(fn, delay = 300) {
      let timer;
      return function (...args) {
        clearTimeout(timer);
        timer = setTimeout(() => fn.apply(this, args), delay);
      };
    },

    pill(status) {
      const map = {
        draft:               ['Draft',             'neutral'],
        confirmed:           ['Confirmed',         'warning'],
        picking:             ['Picking',           'info'],
        packed:              ['Packed',            'accent'],
        dispatched:          ['Dispatched',        'purple'],
        delivered:           ['Delivered',         'success'],
        failed:              ['Failed',            'danger'],
        lost:                ['Lost',              'danger'],
        cancelled:           ['Cancelled',         'neutral'],
        delayed:             ['Delayed',           'danger'],
        received:            ['Received',          'warning'],
        collected:           ['Collected',         'success'],
        return_requested:    ['Return Requested',  'warning'],
        return_received:     ['Return Received',   'info'],
        refunded:            ['Refunded',          'neutral'],
        unpaid:              ['Unpaid',            'danger'],
        partial:             ['Partial',           'warning'],
        paid:                ['Paid',              'success'],
        overdue:             ['Overdue',           'danger'],
        disputed:            ['Disputed',          'warning'],
        refunded_payment:    ['Refunded',          'neutral'],
        ordered:             ['Ordered',           'neutral'],
        in_transit:          ['In Transit',        'warning'],
        customs:             ['Customs',           'warning'],
        cleared:             ['Cleared',           'success'],
        grn_pending:         ['GRN Pending',       'warning'],
        in_warehouse:        ['In Warehouse',      'success'],
        approved:            ['Approved',          'info'],
        sent:                ['Sent',              'info'],
        partially_received:  ['Part Received',     'warning'],
        completed:           ['Completed',         'success'],
        raised:              ['Raised',            'warning'],
        acknowledged:        ['Acknowledged',      'info'],
        settled:             ['Settled',           'success'],
        rejected:            ['Rejected',          'danger'],
        escalated_14:        ['14d Escalated',     'warning'],
        overdue_30:          ['30d Overdue',       'danger'],
        active:              ['Active',            'success'],
        inactive:            ['Inactive',          'neutral'],
        good:                ['Good',              'success'],
        damaged:             ['Damaged',           'danger'],
        missing:             ['Missing',           'danger'],
        // Invoice statuses
        voided:              ['Voided',            'neutral'],
        partially_paid:      ['Part Paid',         'warning'],
      };
      const [label, type] = map[status] || [status || '—', 'neutral'];
      if (type === 'purple') {
        return `<span class="ims__pill" style="background:#ede9fe;color:#6d28d9">${label}</span>`;
      }
      return `<span class="ims__pill ims__pill--${type}">${label}</span>`;
    },

    buildTrackingUrl(courier, trackingNumber, postcode) {
      if (!courier?.tracking_url_base || !trackingNumber) return null;
      let url = courier.tracking_url_base + encodeURIComponent(trackingNumber.trim());
      if (courier.requires_postcode && postcode) {
        const pc = postcode.replace(/\s/g, '').toUpperCase();
        const param = courier.postcode_param || '&postcode=';
        url += param + encodeURIComponent(pc);
      }
      return url;
    },

    // ─── BOX SUMMARY HELPER ─────────────────────────────────────────
    async getOrderBoxSummary(orderItems) {
      const empty = { totalBoxes: 0, totalWeightKg: 0, totalVolWtKg: 0, hasData: false, lines: [] };
      if (!orderItems || !orderItems.length) return empty;

      const productIds = [...new Set(orderItems.map(i => i.product_id).filter(Boolean))];
      if (!productIds.length) return empty;

      const client = window.supabase.createClient(window.IMS_SUPABASE_URL, window.IMS_SUPABASE_ANON_KEY);
      const { data: allBoxes, error } = await client
        .from('product_boxes')
        .select('product_id, box_number, label, length_cm, width_cm, height_cm, weight_kg')
        .in('product_id', productIds)
        .order('product_id')
        .order('box_number');

      if (error || !allBoxes || !allBoxes.length) return empty;

      const boxMap = {};
      allBoxes.forEach(b => {
        if (!boxMap[b.product_id]) boxMap[b.product_id] = [];
        boxMap[b.product_id].push(b);
      });

      let totalBoxes = 0, totalWeightKg = 0, totalVolWtKg = 0, hasData = false;

      const lines = orderItems.map(item => {
        const productBoxes = boxMap[item.product_id] || [];
        const qty = item.quantity || 1;

        if (!productBoxes.length) {
          return {
            productId: item.product_id,
            productName: item.product_name || item.products?.name || '—',
            sku: item.sku || item.products?.sku || '—',
            quantity: qty, boxes: [],
            lineBoxes: 0, lineWeightKg: 0, lineVolWtKg: 0
          };
        }

        hasData = true;
        const lineBoxes = productBoxes.length * qty;
        const lineWeightKg = productBoxes.reduce((s, b) => s + (b.weight_kg || 0), 0) * qty;
        const lineVolWtKg = productBoxes.reduce((s, b) => {
          return s + (((b.length_cm || 0) * (b.width_cm || 0) * (b.height_cm || 0)) / 5000);
        }, 0) * qty;

        totalBoxes    += lineBoxes;
        totalWeightKg += lineWeightKg;
        totalVolWtKg  += lineVolWtKg;

        return {
          productId: item.product_id,
          productName: item.product_name || item.products?.name || '—',
          sku: item.sku || item.products?.sku || '—',
          quantity: qty, boxes: productBoxes,
          lineBoxes,
          lineWeightKg: parseFloat(lineWeightKg.toFixed(2)),
          lineVolWtKg:  parseFloat(lineVolWtKg.toFixed(2))
        };
      });

      return {
        totalBoxes,
        totalWeightKg: parseFloat(totalWeightKg.toFixed(2)),
        totalVolWtKg:  parseFloat(totalVolWtKg.toFixed(2)),
        hasData, lines
      };
    },

    // ─── VAT HELPERS ─────────────────────────────────────────────────
    // Calculate net amount from gross (VAT-inclusive)
    grossToNet(gross, vatRate = 20) {
      return parseFloat((gross / (1 + vatRate / 100)).toFixed(2));
    },
    // Calculate VAT amount from net
    netToVat(net, vatRate = 20) {
      return parseFloat((net * vatRate / 100).toFixed(2));
    },
    // Calculate gross from net
    netToGross(net, vatRate = 20) {
      return parseFloat((net * (1 + vatRate / 100)).toFixed(2));
    },
    // Format VAT rate for display
    formatVatRate(rate) {
      return rate === 0 ? 'Zero rated' : rate === 20 ? 'Standard 20%' : `${rate}%`;
    },

    print(html, title) {
      const c = AUTH.company;
      const win = window.open('', '_blank', 'width=900,height=700');
      if (!win) { UI.toast('Pop-up blocked. Please allow pop-ups for this site.', 'warning'); return; }
      const isCustomLayout = html.includes('<style>');
      if (isCustomLayout) {
        win.document.write(`<!DOCTYPE html><html lang="en"><head>
          <meta charset="UTF-8"><title>${this.sanitize(title)}</title>
        </head><body>${html}</body></html>`);
      } else {
        win.document.write(`<!DOCTYPE html><html lang="en"><head>
          <meta charset="UTF-8"><title>${this.sanitize(title)} | StockMaestro</title>
          <style>
            *{margin:0;padding:0;box-sizing:border-box}
            body{font-family:Arial,sans-serif;font-size:12px;color:#111;padding:20px;line-height:1.4}
            .ph{display:flex;justify-content:space-between;align-items:flex-start;padding-bottom:12px;border-bottom:2px solid #1B3A6B;margin-bottom:18px}
            .ph-name{font-size:20px;font-weight:700;color:#1B3A6B}
            .ph-co{font-size:11px;color:#555;margin-top:2px}
            .ph-right{text-align:right;font-size:11px;color:#555}
            h2{color:#1B3A6B;font-size:14px;margin:14px 0 8px}
            table{width:100%;border-collapse:collapse;margin:8px 0;font-size:11px}
            th{background:#1B3A6B;color:#fff;padding:5px 8px;text-align:left}
            td{padding:4px 8px;border-bottom:1px solid #eee}
            tr:nth-child(even) td{background:#f9f9f9}
            .pf{margin-top:20px;padding-top:8px;border-top:1px solid #ccc;font-size:10px;color:#999;text-align:center}
            .pill{display:inline-block;padding:1px 7px;border-radius:999px;font-size:10px;background:#e5e7eb;color:#374151}
            @media print{body{padding:10px}.no-print{display:none}}
          </style></head><body>
          <div class="ph">
            <div>
              <div class="ph-name">StockMaestro</div>
              <div class="ph-co">${this.sanitize(c?.name || '')}${c?.vat_number ? ' | VAT: ' + c.vat_number : ''}</div>
              ${c?.address ? `<div class="ph-co">${this.sanitize(c.address)}</div>` : ''}
            </div>
            <div class="ph-right">
              <strong>${this.sanitize(title)}</strong><br>
              ${new Intl.DateTimeFormat('en-GB', { dateStyle: 'full', timeStyle: 'short' }).format(new Date())}
            </div>
          </div>
          ${html}
          <div class="pf">StockMaestro &copy; 2026 ABSA Digital. All rights reserved. | ${this.sanitize(title)}</div>
          </body></html>`);
      }
      win.document.close();
      setTimeout(() => win.print(), 500);
    },

    exportCSV(headers, rows, filename) {
      const lines = [
        headers.map(h => `"${h}"`).join(','),
        ...rows.map(row => row.map(c => `"${String(c ?? '').replace(/"/g, '""')}"`).join(','))
      ];
      const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `stockmaestro_${filename}_${new Date().toLocaleDateString('en-GB').replace(/\//g, '-')}.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
    },

    parseCSV(text) {
      const lines = text.split(/\r?\n/).filter(l => l.trim());
      if (!lines.length) return { headers: [], rows: [] };
      const parse = line => {
        const result = []; let current = ''; let inQuotes = false;
        for (let i = 0; i < line.length; i++) {
          if (line[i] === '"') {
            if (inQuotes && line[i + 1] === '"') { current += '"'; i++; }
            else inQuotes = !inQuotes;
          } else if (line[i] === ',' && !inQuotes) {
            result.push(current.trim()); current = '';
          } else current += line[i];
        }
        result.push(current.trim());
        return result;
      };
      const headers = parse(lines[0]);
      const rows = lines.slice(1).map(l => parse(l));
      return { headers, rows };
    }
  };

  // ─── KEEP-ALIVE PING ─────────────────────────────────────────────
  function keepAlive() {
    setInterval(async () => {
      const client = sb();
      if (client && AUTH.company?.id) {
        await client.from('companies').select('id').eq('id', AUTH.company.id).limit(1);
      }
    }, 23 * 60 * 60 * 1000);
  }

  // ─── BOOT ─────────────────────────────────────────────────────────
  async function enterApp() {
    // Licence check before showing app
    const licenceStatus = await checkLicence();
    if (licenceStatus === 'suspended' || licenceStatus === 'expired') return; // lockout screen shown
    UI.showApp();
    const hash = location.hash.replace('#', '') || 'dashboard';
    UI.showPanel(hash);
    keepAlive();
    if (AUTH.company?.id) {
      const client = sb();
      if (client) await client.rpc('check_claim_alerts', { p_company_id: AUTH.company.id });
      UI.loadNotifications();
    }
    window.dispatchEvent(new CustomEvent('ims:ready'));
  }

  async function boot() {
    UI.init();

    // Invite / password-reset links redirect here with a Supabase auth
    // token in the URL hash (e.g. #access_token=...&type=invite&...).
    // The client (detectSessionInUrl:true, confirmed default — no
    // override set in sb()) silently establishes a session from this
    // token. Without this check, a newly invited user would either get
    // dropped straight into the full app with no password ever set (and
    // no way to log back in later), or bounce to a normal login screen
    // with nothing to sign in with — this intercepts both invite and
    // recovery links first and requires setting a password before
    // proceeding any further.
    const hashParams = new URLSearchParams(location.hash.replace('#', '?'));
    const flowType = hashParams.get('type');
    if (flowType === 'invite' || flowType === 'recovery') {
      UI.showSetPassword();
      return;
    }

    const hasSession = await AUTH.checkSession();
    if (hasSession) {
      await enterApp();
    } else {
      UI.showLogin();
    }
  }

  // ─── SET PASSWORD HANDLER (invite / reset links) ──────────────────
  function bindSetPassword() {
    const btn     = document.getElementById('ims-setpw-btn');
    const passEl  = document.getElementById('ims-setpw-password');
    const confEl  = document.getElementById('ims-setpw-confirm');
    const errEl   = document.getElementById('ims-setpw-error');
    const btnText = document.getElementById('ims-setpw-btn-text');

    async function doSetPassword() {
      const pass = passEl?.value;
      const conf = confEl?.value;
      if (errEl) errEl.style.display = 'none';

      if (!pass || pass.length < 8) {
        if (errEl) { errEl.textContent = 'Password must be at least 8 characters.'; errEl.style.display = 'block'; }
        return;
      }
      if (pass !== conf) {
        if (errEl) { errEl.textContent = 'Passwords do not match.'; errEl.style.display = 'block'; }
        return;
      }

      if (btn) btn.disabled = true;
      if (btnText) btnText.textContent = 'Setting password...';

      const client = sb();
      if (!client) {
        if (errEl) { errEl.textContent = 'Connection error — please try again.'; errEl.style.display = 'block'; }
        if (btn) btn.disabled = false;
        if (btnText) btnText.textContent = 'Set password & continue';
        return;
      }

      const { error } = await client.auth.updateUser({ password: pass });
      if (error) {
        if (errEl) { errEl.textContent = error.message || 'Failed to set password. The link may have expired — ask your administrator to resend the invite.'; errEl.style.display = 'block'; }
        if (btn) btn.disabled = false;
        if (btnText) btnText.textContent = 'Set password & continue';
        return;
      }

      // Password set successfully — the session established by the
      // invite/recovery token is still active. Clear the token from the
      // URL first so a subsequent boot() call doesn't re-detect
      // type=invite and loop back to this same screen, then proceed
      // through the normal session flow (which now correctly links the
      // account via loadProfile()'s email fallback) into the app.
      history.replaceState(null, '', location.pathname + location.search);
      const hasSession = await AUTH.checkSession();
      if (hasSession) {
        await enterApp();
      } else {
        UI.showLogin();
      }
    }

    btn?.addEventListener('click', doSetPassword);
    confEl?.addEventListener('keydown', e => { if (e.key === 'Enter') doSetPassword(); });
  }

  // ─── LOGIN HANDLER ────────────────────────────────────────────────
  function bindLogin() {
    const btn     = document.getElementById('ims-login-btn');
    const emailEl = document.getElementById('ims-login-email');
    const passEl  = document.getElementById('ims-login-password');
    const errEl   = document.getElementById('ims-login-error');
    const btnText = document.getElementById('ims-login-btn-text');

    let _loginAttempts = 0;
    let _loginLockedUntil = 0;

    async function doLogin() {
      const email = emailEl?.value?.trim();
      const pass  = passEl?.value;
      if (errEl) errEl.style.display = 'none';

      // Rate limiting — 5 attempts then 60 second lockout
      if (Date.now() < _loginLockedUntil) {
        const secs = Math.ceil((_loginLockedUntil - Date.now()) / 1000);
        if (errEl) { errEl.textContent = `Too many attempts. Try again in ${secs} seconds.`; errEl.style.display = 'block'; }
        return;
      }

      if (!email || !pass) {
        if (errEl) { errEl.textContent = 'Please enter your email and password.'; errEl.style.display = 'block'; }
        return;
      }
      if (btn) btn.disabled = true;
      if (btnText) btnText.textContent = 'Signing in...';

      const result = await AUTH.signIn(email, pass);
      if (result.error) {
        _loginAttempts++;
        if (_loginAttempts >= 5) {
          _loginLockedUntil = Date.now() + 60000;
          _loginAttempts = 0;
          if (errEl) { errEl.textContent = 'Too many failed attempts. Please wait 60 seconds.'; errEl.style.display = 'block'; }
        } else {
          if (errEl) { errEl.textContent = result.error; errEl.style.display = 'block'; }
        }
        if (btn) btn.disabled = false;
        if (btnText) btnText.textContent = 'Sign in';
      } else {
        const licenceStatus = await checkLicence();
        if (licenceStatus === 'suspended' || licenceStatus === 'expired') return;
        UI.showApp();
        UI.showPanel('dashboard');
        keepAlive();
        if (AUTH.company?.id) {
          const client = sb();
          if (client) await client.rpc('check_claim_alerts', { p_company_id: AUTH.company.id });
          UI.loadNotifications();
        }
        window.dispatchEvent(new CustomEvent('ims:ready'));
      }
    }

    btn?.addEventListener('click', doLogin);
    passEl?.addEventListener('keydown', e => { if (e.key === 'Enter') doLogin(); });
    document.getElementById('ims-logout-btn')?.addEventListener('click', () => AUTH.signOut());
  }

  // ─── EXPOSE GLOBALS ──────────────────────────────────────────────
  window.SM = { IMS, AUTH, DB, UI, UTIL };
  window.IMS = window.SM;

  // ─── INIT ────────────────────────────────────────────────────────
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => { bindLogin(); bindSetPassword(); boot(); });
  } else {
    bindLogin(); bindSetPassword(); boot();
  }

})();
